import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import cn from 'classnames';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { FiFlag } from "react-icons/fi";
import { Card, DateField, Typography, Button, Box, CardHeader, CardTitle } from '@otcmarketsgroup/otcm-ui-lib';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemeContext } from '../../context/Theme.context';
import { getBlueSkyData, getBlueSkySummary, getHistoricalBlueSkyData, getHistoricalBlueSkySummary } from '../../api/bluesky';
import { getSecurities } from '../../api/company';
import { format } from '../../utils/locale';
import { isWeekend, calculateBlueskyScorePercentage, SCORE_TYPE } from '../../utils/helper';
import ErrorPage from '../ErrorPage';
import { withRouter } from '../../components/WithRouter';
import Loading from '../../components/Loading';
import BlueSkyComplianceTable from '../../components/BlueSkyComplianceTable';
import BlueSkyComplianceMap from '../../components/BlueSkyComplianceMap';
import BlueSkySummary from '../../components/BlueSkySummary';
import styles from './BlueSkyMonitor.module.scss';
import { storage } from '../../constants';
import { ROUTES } from '../../constants/routes';

const minBlueSkyDate = '07/01/2020';
const minBlueSkyDateObj = dayjs(minBlueSkyDate);

dayjs.extend(utc);
dayjs.extend(timezone);

const BlueSkyMonitor = ({ params }) => {
    const [themeState] = useContext(ThemeContext);
    const [blueSkyStats, setBlueSkyStats] = useState(null);
    const [blueSkyData, setBlueSkyData] = useState(null);
    const [asOfDate, setAsOfDate] = useState(null);
    const [recentDate, setRecentDate] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [reloading, setReloading] = useState(false);
    const [currentSecurity, setCurrentSecurity] = useState('');
    const [securities, setSecurities] = useState(null);
    const [blueskyError, setBlueSkyError] = useState('');
    const [helperText, setHelperText] = useState('');
    const [showErrorPage, setErrorPage] = useState(false);
    const symbol = params?.symbol;
    const blueskyLoaded = !!blueSkyStats && !!blueSkyData;
    const accentColor = themeState.color;
    const hasAdditionalSecurities = securities && securities.length > 0;

    useEffect(() => {
        window.sessionStorage.setItem(storage.SYMBOL, symbol);

        if (asOfDate) setReloading(true);

        getSecurities()
            .then(data => {
                const currentSecurity = data.find(item => item.symbol.toLowerCase() === symbol.toLowerCase());
                const otherSecurities = data.filter(item => item.symbol.toLowerCase() !== symbol.toLowerCase());
                setCurrentSecurity(currentSecurity.company?.name);
                setSecurities(otherSecurities);
            })
            .catch(error => console.error(`Failed Securities call: `, error));

        loadLatestBlueSkyData();
    }, [symbol]);

    const loadLatestBlueSkyData = _ => {
        const apiSummaryParams = {
            symbol,
            complianceStatus: 'BOTH'
        };

        const apiDataParams = {
            symbol
        };

        if (asOfDate) setReloading(true);

        Promise.all([getBlueSkySummary(apiSummaryParams), getBlueSkyData(apiDataParams)])
            .then(([summaryData, blueskyData]) => {
                const asOfDate = format(blueskyData?.blueskyDate, 'N/A', 'staticDate');

                setRecentDate(asOfDate);
                setAsOfDate(asOfDate);
                setSelectedDate(asOfDate);
                setReloading(false);
                setBlueSkyData(blueskyData?.results);
                setBlueSkyStats(summaryData);
            })
            .catch(error => {
                if (error?.response?.status === 403) {
                    setErrorPage(true);
                    return;
                }

                setReloading(false);
                setBlueSkyError(error?.response?.data?.error || 'Error fetching Blue Sky Data.');
            });
    };

    const loadHistoryicalBlueSkyData = date => {
        setReloading(true);

        const apiParams = {
            symbol,
            date: format(date, date, 'isoDate')
        };

        Promise.all([getHistoricalBlueSkySummary(apiParams), getHistoricalBlueSkyData(apiParams)])
            .then(([summaryData, blueskyData]) => {
                const asOfDate = format(date, 'N/A', 'staticDate');

                setAsOfDate(asOfDate);
                setReloading(false);
                setBlueSkyData(blueskyData?.results);
                setBlueSkyStats(summaryData);
            })
            .catch(error => {
                setReloading(false);
                console.error(err, 'data error');
            });
    };

    const handleDateChange = date => {

        if (!dayjs(date).isValid()) {
            setHelperText('Enter a valid date.');
            return;
        }

        setHelperText('');
        const enteredDate = dayjs(date).format('YYYY/MM/DD');

        if (isWeekend(enteredDate)) {
            setSelectedDate(selectedDate);
        } else {
            const isSameDate = format(recentDate, recentDate, 'staticDate') === format(enteredDate, enteredDate, 'staticDate');

            if (isSameDate && new Date(recentDate).getTime() === new Date(enteredDate).getTime()) {
                loadLatestBlueSkyData();
            } else {
                loadHistoryicalBlueSkyData(enteredDate);
            }

            setSelectedDate(enteredDate);
        }
    };

    const blueskyScorePercent = calculateBlueskyScorePercentage(
        blueSkyStats?.blueskyScoreIn,
        blueSkyStats?.blueskyScoreOut,
        SCORE_TYPE.IN
    );

    return showErrorPage ? <ErrorPage /> : <div className={styles.container}>
        <Box display='flex' flexDirection='column' gap={2}>
            <Card>
                <CardHeader>
                    <CardTitle icon={<FiFlag />}>
                        Blue Sky Monitor
                    </CardTitle>
                    <div className={styles.dateField}>
                        {asOfDate && <DateField
                            value={selectedDate}
                            onSelectDate={handleDateChange}
                            shouldDisableDate={isWeekend}
                            minDate={minBlueSkyDateObj}
                            maxDate={new dayjs()}
                            helperText={helperText}
                        />}
                    </div>
                </CardHeader>
                <Loading loaded={blueskyLoaded} reloading={reloading} error={blueskyError}>
                    <BlueSkySummary className={cn(styles.summaryMobile, 'mbXL')} data={blueSkyStats} />
                    <BlueSkyComplianceMap className='mbXL' data={blueSkyData} accent={accentColor} loaded={blueskyLoaded} blueskyScorePercent={blueskyScorePercent} />
                    <BlueSkyComplianceTable data={blueSkyData} asOfDate={asOfDate} symbol={symbol} />
                </Loading>
            </Card>

            <Card>
                <Typography variant='h2'>
                    Blue Sky Monitoring
                </Typography>
                <div className={styles.note}>
                    <Typography variant='body1'>
                        The Blue Sky Monitoring Service offers a customized, daily audit of your company's compliance status in each U.S. state or territory. Broker dealers must confirm that securities are compliant with the Blue Sky laws of a given jurisdiction before recommending them to investors residing there. This service will help you identify compliance barriers that may restrict trading in your company's stock.
                    </Typography>
                </div>
                <Link to={`${ROUTES.blueSkyRulesPath}/alaska`} className={styles.linkSpacing}>
                    <Button endIcon={<FontAwesomeIcon icon={{ icon: [320, 512, [9002], 'f054', 'M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z'], iconName: 'chevron-right', prefix: 'fas' }} />}>
                        Blue Sky Rules
                    </Button>
                </Link>
                <a href={ROUTES.stateSecuritiesCommissionsUrl} target='_blank' rel='noopener noreferrer'>
                    <Button endIcon={<FontAwesomeIcon icon={{ icon: [320, 512, [9002], 'f054', 'M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z'], iconName: 'chevron-right', prefix: 'fas' }} />} >
                        State Securities Commissions
                    </Button>
                </a>
            </Card>
        </Box>
    </div>;
};

BlueSkyMonitor.propTypes = {
    params: PropTypes.object,
};

export default withRouter(BlueSkyMonitor);